import cx from 'clsx';
import React, { useEffect, useState } from 'react';

import { useDebounce } from '../../hooks/useDebounce';
import { SearchIcon } from '../icons';
import styles from './SearchInput.module.scss';
import { useWindowSize } from '../../hooks/useWindowSize';
import { SMALL_BREAKPOINT } from '../modular-grid/constants';

interface SearchInputProps {
  state: 'active' | 'inactive';
  searchPhase?: 'processing' | 'finished';
  placeholderText_New: {
    default: string;
    active: string;
  };
  clearText: string;
  searchLink?: string;
  onChange?: (args: { value: string }) => void;
  onClearButtonClick?: (args: { value: string }) => void;
  onSearchButtonClick?: (args: { value: string }) => void;
  onSearchWrapperClick?: (args: { path: string }) => void;
  onSoftKeyboardVisibilityChange?: (args: {
    behavior: 'open' | 'close';
  }) => void;
  debounceTimeout?: number;
  searchPhrase?: string;
  inputConfig?: {
    isIOS: boolean;
    isRenderedInIFrame: boolean;
  };
}

const SearchInput: React.FC<SearchInputProps> = ({
  state,
  placeholderText_New,
  clearText,
  searchLink,
  onChange,
  onClearButtonClick,
  onSearchButtonClick,
  onSearchWrapperClick,
  onSoftKeyboardVisibilityChange,
  debounceTimeout = 350,
  inputConfig = {
    isIOS: false,
    isRenderedInIFrame: false,
  },
}) => {
  const windowWidth = useWindowSize().width;
  const [phrase, setPhrase] = useState<string>('');
  const [searchInputFocused, setSearchInputFocused] = useState<boolean>(
    !onSearchWrapperClick
  );

  const debouncedPhrase = useDebounce(phrase, debounceTimeout);
  useEffect(() => {
    if (debouncedPhrase && phrase) {
      onChange && onChange({ value: debouncedPhrase });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPhrase]);

  useEffect(() => {
    if (
      inputConfig.isIOS &&
      inputConfig.isRenderedInIFrame &&
      windowWidth <= SMALL_BREAKPOINT.max
    ) {
      onSoftKeyboardVisibilityChange &&
        onSoftKeyboardVisibilityChange({
          behavior: searchInputFocused ? 'open' : 'close',
        });
    }
  }, [
    searchInputFocused,
    inputConfig,
    onSoftKeyboardVisibilityChange,
    windowWidth,
  ]);

  const onFocus = () => setSearchInputFocused(true);
  const onBlur = () => setSearchInputFocused(false);

  const searchPlaceholderText =
    placeholderText_New &&
    (searchInputFocused
      ? placeholderText_New.active
      : placeholderText_New.default);

  useEffect(() => {
    setStatePlaceholderText(searchPlaceholderText);
  }, [searchPlaceholderText]);

  const [statePlaceholderText, setStatePlaceholderText] = useState<string>(
    searchPlaceholderText
  );
  const createClickHandler = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onSearchWrapperClick && onSearchWrapperClick({ path: searchLink || '' });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPhrase(event.target.value);
  };

  const handleSearchClick = () => {
    onSearchButtonClick!({ value: phrase });
    setPhrase('');
  };

  const handleClearClick = () => {
    onClearButtonClick && onClearButtonClick({ value: '' });
    setPhrase('');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearchButtonClick!({ value: phrase });
      setPhrase('');
    }
  };

  const handleInputClick = () => {
    setStatePlaceholderText('');
    setPhrase('');
  };

  if (onSearchWrapperClick) {
    return (
      <a
        className={cx(
          styles.search,
          state === 'active' ? styles.searchActive : styles.searchInactive
        )}
        data-testid="search-wrapper"
        onClick={createClickHandler}
        href={searchLink}
        rel={'nofollow'}
      >
        <input
          className={styles.input}
          autoFocus={false}
          readOnly={true}
          placeholder={statePlaceholderText}
          onKeyDown={e => handleKeyDown(e)}
          onFocus={onFocus}
          onBlur={onBlur}
          data-testid="search-input"
        />
        <div className={styles.iconWrapper} data-testid="search-icon">
          <SearchIcon className={styles.iconInactive} />
        </div>
      </a>
    );
  }

  const clearVisible = phrase !== '' || state === 'active';

  return (
    <div
      data-testid="search-wrapper"
      className={cx(
        styles.search,
        state === 'active' ? styles.searchActive : styles.searchInactive
      )}
    >
      <input
        className={styles.input}
        autoFocus={true}
        readOnly={false}
        placeholder={statePlaceholderText === '' ? '' : statePlaceholderText}
        onChange={handleChange}
        onClick={handleInputClick}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        value={phrase}
        data-testid="search-input"
        maxLength={50}
      />
      {clearVisible && (
        <span
          className={styles.clear}
          data-testid="search-clear"
          onClick={handleClearClick}
        >
          {clearText}
        </span>
      )}
      <div
        data-testid="search-icon"
        className={styles.iconWrapper}
        onClick={handleSearchClick}
      >
        <SearchIcon />
      </div>
    </div>
  );
};

export default SearchInput;
